import { debugLog } from "../config";
import type { ExtractedContent } from "../types";
import { maxContentLength, maxImages, noiseSelectors } from "./constants";
import { normalizeWhitespace } from "./formatters";

/**
 * Extracts the document title.
 */
function extractTitle(): string {
  return normalizeWhitespace(document.title || "");
}

/**
 * Extracts meta description from <meta name="description">.
 */
function extractDescription(): string {
  const meta = document.querySelector('meta[name="description"]');
  const content = meta?.getAttribute("content") ?? "";
  return normalizeWhitespace(content);
}

/**
 * Extracts keywords from <meta name="keywords"> as an array.
 * Keywords are split by comma and individually trimmed.
 */
function extractKeywords(): string[] {
  const meta = document.querySelector('meta[name="keywords"]');
  const content = meta?.getAttribute("content") ?? "";

  if (!content.trim()) {
    return [];
  }

  return content
    .split(",")
    .map((keyword) => keyword.trim())
    .filter(Boolean);
}

/**
 * Collects all h1-h6 heading text content.
 */
function extractHeadings(): string[] {
  const headings = document.querySelectorAll("h1, h2, h3, h4, h5, h6");

  return Array.from(headings)
    .map((heading) => normalizeWhitespace(heading.textContent ?? ""))
    .filter(Boolean);
}

/**
 * Returns true when the element matches any selector in `noiseSelectors`.
 * Invalid selectors are skipped (logged in debug) so a single broken entry
 * can't blow up the whole walk.
 */
function isNoiseElement(el: Element): boolean {
  for (const selector of noiseSelectors) {
    try {
      if (el.matches(selector)) return true;
    } catch (error) {
      debugLog(`Invalid noise selector "${selector}":`, error);
    }
  }
  return false;
}

/**
 * Walks `root` with a TreeWalker that skips noise subtrees (when filtered)
 * and accumulates visible text. We collect a generous raw budget (2× the
 * char cap) so whitespace normalization downstream can still land near
 * `maxContentLength` without us walking the entire DOM on very long pages.
 *
 * Trade-off: a page whose first ~2× cap of raw chars is dominated by
 * whitespace will collapse to less content than the previous
 * `cloneNode + textContent` path collected (the old path walked the entire
 * body before truncating). For article-heavy publishers — the audience most
 * likely to opt into ingestion — this is acceptable. If a future "shorter
 * content" report emerges, the safety multiplier can be raised here.
 */
function walkAndExtractText(
  root: Element,
  filterNoise: boolean,
  maxLength: number,
): string {
  const walker = document.createTreeWalker(
    root,
    NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT,
    {
      acceptNode: (node: Node): number => {
        if (node.nodeType === Node.ELEMENT_NODE) {
          if (filterNoise && isNoiseElement(node as Element)) {
            return NodeFilter.FILTER_REJECT;
          }
          return NodeFilter.FILTER_SKIP;
        }
        return NodeFilter.FILTER_ACCEPT;
      },
    },
  );

  const parts: string[] = [];
  let rawCharCount = 0;
  const rawCap = maxLength * 2;

  let node = walker.nextNode();
  while (node && rawCharCount < rawCap) {
    const text = node.textContent ?? "";
    parts.push(text);
    rawCharCount += text.length;
    node = walker.nextNode();
  }

  const normalized = normalizeWhitespace(parts.join(""));
  return normalized.length > maxLength
    ? normalized.slice(0, maxLength)
    : normalized;
}

/**
 * Extracts main content with priority: <main> > <article> > <body>.
 *
 * Walks the chosen root with a TreeWalker that rejects noise subtrees (nav,
 * footer, ads, scripts, styles, `[hidden]` / `[aria-hidden="true"]`, etc.)
 * without ever cloning or mutating the live DOM. Noise filtering applies
 * uniformly — even inside `<main>` / `<article>`, a hidden div or script
 * block is still skipped. Content is truncated to `maxContentLength`
 * characters after whitespace normalization.
 */
function extractMainContent(): string {
  const mainElement = document.querySelector("main");
  if (mainElement) {
    return walkAndExtractText(mainElement, true, maxContentLength);
  }

  const articleElement = document.querySelector("article");
  if (articleElement) {
    return walkAndExtractText(articleElement, true, maxContentLength);
  }

  return walkAndExtractText(document.body, true, maxContentLength);
}

/**
 * Extracts alt text from the first maxImages images.
 */
function extractImageAltTexts(): string[] {
  const images = document.querySelectorAll("img[alt]");

  return Array.from(images)
    .slice(0, maxImages)
    .map((img) => img.getAttribute("alt")?.trim() ?? "")
    .filter(Boolean);
}

/**
 * Parses JSON-LD structured data from script tags.
 * Invalid JSON is silently skipped.
 */
function extractStructuredData(): Record<string, unknown>[] {
  const scripts = document.querySelectorAll(
    'script[type="application/ld+json"]',
  );
  const data: Record<string, unknown>[] = [];

  scripts.forEach((script) => {
    try {
      const content = script.textContent?.trim();
      if (content) {
        const parsed = JSON.parse(content) as Record<string, unknown>;
        data.push(parsed);
      }
    } catch (error) {
      debugLog("Failed to parse JSON-LD structured data:", error);
    }
  });

  return data;
}

/**
 * Extracts semantic content from the host page's DOM.
 *
 * Collects title, meta tags, headings, main content, images,
 * structured data, and URL information while filtering out
 * navigation, ads, and other noise elements.
 *
 * @returns Typed ExtractedContent object with all extracted fields
 */
export function extractPageContent(): ExtractedContent {
  return {
    title: extractTitle(),
    description: extractDescription(),
    keywords: extractKeywords(),
    headings: extractHeadings(),
    mainContent: extractMainContent(),
    images: extractImageAltTexts(),
    structuredData: extractStructuredData(),
    url: window.location.href,
    path: window.location.pathname + window.location.search,
  };
}
